/** Pure helpers for the Materials tab: grouping by type, labels and safe link targets. */

import { MaterialIn } from '@/api/generated/schemas';
import { MATERIAL_TYPES, type MaterialOut, type MaterialType } from '@/api/types/materials';
import { fieldErrors } from '@/api/validate';

export interface MaterialGroup {
  type: MaterialType;
  label: string;
  current: MaterialOut[];
  older: MaterialOut[];
}

export function materialTypeLabel(type: string): string {
  return MATERIAL_TYPES.find((t) => t.value === type)?.label ?? 'Other';
}

const byPosition = (a: MaterialOut, b: MaterialOut) => a.position - b.position || a.title.localeCompare(b.title);

/** The materials by type, in the fixed type order; superseded ones apart, as older versions. Empty types drop out. */
export function groupMaterials(materials: MaterialOut[]): MaterialGroup[] {
  return MATERIAL_TYPES.flatMap(({ value, label }) => {
    const ofType = materials.filter((m) => (MATERIAL_TYPES.some((t) => t.value === m.material_type) ? m.material_type : 'other') === value);
    if (ofType.length === 0) return [];
    return [
      {
        type: value,
        label,
        current: ofType.filter((m) => !m.superseded_by).sort(byPosition),
        older: ofType.filter((m) => m.superseded_by).sort(byPosition),
      },
    ];
  });
}

/** An address worth turning into a link: only http(s), so a stored `javascript:` cannot run. */
export function safeHref(url: string | null): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
}

export function linkHost(url: string | null): string {
  if (!url) return 'No address';
  try {
    return new URL(url).host || url;
  } catch {
    return url;
  }
}

/** A title to start from: the file name without its extension. */
export function titleFromFilename(name: string): string {
  return name.replace(/\.[a-z0-9]+$/i, '').trim() || name;
}


/** What a person fills in for a material; the file id and the revision come from the server. */
const TYPED = MaterialIn.pick({ title: true, material_type: true, audience: true, url: true });

/** Whether the API would take these fields; a link must also be an http or https address. */
export function materialValid(fields: { title: string; material_type: string; audience: string; url?: string }): boolean {
  if (fields.url !== undefined && safeHref(fields.url.trim()) === null) return false;
  return Object.keys(fieldErrors(TYPED, fields)).length === 0;
}
